import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import type { Column } from '../../components/ui/table';

const statusLabels = (): Record<string, string> => ({
  draft: t('events.status.draft'), published: t('events.status.published'),
  completed: t('events.status.completed'), cancelled: t('events.status.cancelled'),
});

@customElement('cadence-events-page')
export class CadenceEventsPage extends LitElement {
  @state() private events: any[] = [];
  @state() private types: any[] = [];
  @state() private tags: any[] = [];
  @state() private filter = '';
  @state() private loading = true;
  @state() private showCreate = false;
  @state() private formError = '';
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-4); gap: 12px; flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.6rem; }
    .actions { display: flex; gap: 10px; align-items: center; }
    .filters { display: flex; gap: 6px; margin-bottom: var(--space-4); flex-wrap: wrap; }
    .chip { font: inherit; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); padding: 6px 12px; border-radius: 999px; }
    .chip.active { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.78rem; font-weight: 600; }
    .s-draft { background: var(--color-bg-secondary); color: var(--color-text-secondary); }
    .s-published { background: #dcfce7; color: #166534; }
    .s-completed { background: #dbeafe; color: #1e40af; }
    .s-cancelled { background: #fee2e2; color: #991b1b; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const qs = this.filter ? `?status=${this.filter}` : '';
      const [events, types, tags] = await Promise.all([api.get<any[]>(`/events${qs}`), api.get<any[]>('/events/types'), api.get<any[]>('/tags')]);
      this.events = events; this.types = types; this.tags = tags;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private setFilter(f: string) { this.filter = f; this.load(); }

  private async create(v: any) {
    if (!v.name?.trim()) { this.formError = t('events.toast.eventNameRequired'); return; }
    this.saving = true; this.formError = '';
    try {
      const ev = await api.post<any>('/events', {
        ...v,
        capacity: v.capacity === '' ? null : Number(v.capacity),
        fee_amount: v.fee_amount === '' ? null : Number(v.fee_amount),
      });
      showToast(t('events.toast.eventCreated'), 'success');
      this.showCreate = false;
      router.navigate(`/admin/events/${ev.id}`);
    } catch (e) { this.formError = e instanceof ApiError ? e.message : t('events.toast.failed'); }
    finally { this.saving = false; }
  }

  private columns: Column[] = [
    { key: 'name', label: t('events.list.col.event'), sortable: true },
    { key: 'type_name', label: t('events.list.col.type'), render: (e) => e.type_name ?? '—' },
    { key: 'start_date', label: t('events.list.col.dates'), render: (e) => e.start_date ? (e.end_date && e.end_date !== e.start_date ? `${e.start_date} → ${e.end_date}` : e.start_date) : '—' },
    { key: 'confirmed_count', label: t('events.list.col.going'), align: 'right', render: (e) => `${e.confirmed_count}${e.capacity != null ? '/' + e.capacity : ''}` },
    { key: 'status', label: t('events.list.col.status'), render: (e) => statusLabels()[e.status] ?? e.status },
  ];

  render() {
    return html`
      <div class="head">
        <h1>${t('events.list.title')}</h1>
        <div class="actions">
          <cadence-button @click=${() => { this.showCreate = true; this.formError = ''; }}>${t('events.list.add')}</cadence-button>
        </div>
      </div>

      <div class="filters">
        ${['', 'draft', 'published', 'completed', 'cancelled'].map((f) => html`
          <button class="chip ${this.filter === f ? 'active' : ''}" @click=${() => this.setFilter(f)}>${f === '' ? t('events.list.filterAll') : statusLabels()[f]}</button>`)}
      </div>

      ${this.loading
        ? html`<cadence-loading></cadence-loading>`
        : this.events.length
          ? html`<cadence-table clickable .columns=${this.columns} .rows=${this.events}
              @row-click=${(e: CustomEvent) => router.navigate(`/admin/events/${e.detail.id}`)}></cadence-table>`
          : html`<cadence-empty-state heading=${t('events.list.empty.heading')} message=${t('events.list.empty.message')}></cadence-empty-state>`}

      <cadence-event-form-modal
        ?open=${this.showCreate} heading=${t('events.form.addHeading')} submit-label=${t('events.actions.create')}
        .types=${this.types} .tags=${this.tags} .value=${{ type_id: this.types[0]?.id ?? '', visibility: 'all', tag_ids: [] }}
        ?saving=${this.saving} error=${this.formError}
        @save=${(e: CustomEvent) => this.create(e.detail)}
        @close=${() => (this.showCreate = false)}></cadence-event-form-modal>
    `;
  }
}
